import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { BhctTempProfile, TemperatureProfileType } from '../../models/temperature-profile.model';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models';

interface BhctTempProfileForm {
  type: TemperatureProfileType.BHCT;
  bottomMd: UomQuantityInput;
}

@Component({
    selector: 'app-bhct-temp-profile',
    template: `
    <form [formGroup]="form.formGroup">
        <app-uom-input
          label="Bottom hole circulation depth"
          [unitLabel]="userUnits.longLengths + ' MD'"
          [formControlName]="form.formControlNames.bottomMd"
          [control]="'bottomMd'"
          [validators]="validators?.bottomMd"
          [default]="influxDepthRange.max"
          [inputDisabled]="loadName === 'Initial Condition'"
          minFractionDigits="1"
          (onFocus)="onInputFocus($event)"
          title="Allowed range: {{ influxDepthRange.min }} - {{ influxDepthRange.max }} {{ userUnits.longLengths }}"
      />
    </form>
  `,
    providers: subformComponentProviders(BhctTempProfileComponent),
    standalone: false
})
export class BhctTempProfileComponent implements OnInit {

  @Input() currentString: any;
  @Input() loadName: string;
  @Input() userUnits: UserUnitsModel;
  @Input() tubulars: WellComponent[];

  public validators: any;
  public influxDepthRange: { min: number, max: number };

  public form = createForm<BhctTempProfile, BhctTempProfileForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(TemperatureProfileType.BHCT),
      bottomMd: new UntypedFormControl(null)
    },
    toFormGroup: (obj: BhctTempProfile | null): BhctTempProfileForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        bottomMd: { control: obj.bottomMd }
      }
    },
    fromFormGroup: (formValue: BhctTempProfileForm): BhctTempProfile | null => {
      const { bottomMd, ...commonValues } = formValue;
      return {
        bottomMd: +bottomMd?.control,
        ...commonValues
      }
    }
  });

  ngOnInit() {
    this.setDefaults();
  }

  onInputFocus($event){
    $event.target.select();
  }

  private setDefaults() {
    const selectedStringIndex = this.tubulars.findIndex(x => x.id === this.currentString.id);
    const nextString = this.tubulars[selectedStringIndex + 1];
    if (!nextString) {
      this.influxDepthRange = { min: this.currentString.shoeMd, max: this.currentString.shoeMd };
      return;
    }

    const nextShoeDepth = Math.max(nextString?.shoeMd ?? 0, this.currentString.shoeMd);
    switch (this.currentString.type) {
      case 'Casing': {
        this.checkNextLinerShoeDepth(selectedStringIndex, nextShoeDepth);
        break;
      }
      case 'Liner': {
        this.checkNextLinerShoeDepth(selectedStringIndex, nextShoeDepth);
        break;
      }
      case 'Tieback': {
        const previousString = this.tubulars[selectedStringIndex - 1];
        const previousStringIndex = this.tubulars.findIndex(t => t.shoeMd === previousString.shoeMd) - 1;
        this.checkNextLinerShoeDepth(previousStringIndex, previousString.shoeMd);
        break;
      }
    }
  }

  private checkNextLinerShoeDepth(selectedStringIndex: number, nextOrLinerShoeDepth: number): void {
    let deepestNextLiner = null;
    let tubular = null;
    for (let i = selectedStringIndex + 1; i < this.tubulars.length; i++) {
      tubular = this.tubulars[i];
      if (tubular.type === 'Liner') {
          deepestNextLiner = tubular.shoeMd;
          break;
      } else if (tubular.type === 'Tieback') {
        const linerItsTiedBackTo = this.tubulars.find(t =>
          t.type === 'Liner' &&
          t.hangerMd === tubular.shoeMd
        ).shoeMd;
        deepestNextLiner = linerItsTiedBackTo;
        break;
      } else {
        deepestNextLiner = tubular.shoeMd;
        break;
      }
    }

    this.influxDepthRange = { 
      min: this.currentString.type == 'Tieback' ? nextOrLinerShoeDepth : this.currentString.shoeMd, 
      max: deepestNextLiner ?? this.currentString.shoeMd
    };

    this.update();
  }

  private update(): void {
    const currentStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);
    
    let maxValidation = 0;
    for (let i = currentStringIndex; i < this.tubulars.length; i++) {
      const tubular = this.tubulars[i];
      if (tubular.shoeMd >= this.influxDepthRange.max && i < this.tubulars.length - 1) {

        if (tubular.type == 'Tieback') {
          maxValidation = this.tubulars[i - 1].shoeMd;
          break;
        } else if (tubular.type == 'Liner') {
          maxValidation = tubular.shoeMd;

        for (let j = i - 1; j >= 0; j--) {
          if (this.tubulars[j].type == 'Tieback') {
            continue;
          } else {
            break;
          }
        }
          continue;
        } else if (tubular.type == 'Casing') {
          maxValidation = tubular.shoeMd;
        } else if (tubular.type == 'Tubing') {
          this.influxDepthRange.max = maxValidation;
          break;
        }
        break;
      } else if (tubular.type == 'Tieback') {
        const selectedStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);
        const tiedBackLinerIndex = this.tubulars.findIndex(t =>
          t.type === 'Liner' &&
          t.hangerMd === tubular.shoeMd
        );

        if (selectedStringIndex < tiedBackLinerIndex) {
          maxValidation = this.tubulars[i - 1].shoeMd;
          break;
        }

        maxValidation = this.tubulars[i - 1].shoeMd;
        continue;
      }
    }

    this.influxDepthRange.max = Math.max(this.influxDepthRange.max, maxValidation);

    this.validators = {
      bottomMd: [Validators.required, Validators.min(this.influxDepthRange.min), Validators.max(this.influxDepthRange.max)]
    };
  }
}
